import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import { useRevertAgent } from '@/pages/agents/api';
import { isCancelAgentEditModalOpen } from '@/pages/agents/components/modals';
import { useCompareEditStates } from '@/pages/agents/hooks';
import { useClearEditor } from '@/state/agent';
import { isImageProcessing } from '@/state/presenters/presenters';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { AppPathname, AppRouteNames, AppRoutes, LocationState } from '@/utils/types';
import { AgentCreateTabType, Presenter } from '@d-id/types';
import styled from '@emotion/styled';
import { IconButton, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { HeaderButtons, HeaderButtonsWrapper } from './HeaderButtons';
import { PresentersTabs } from './PresentersTabs';

const HeaderContainer = styled.header`
    width: 100%;
    display: flex;
    background: ${theme.colors.white};
    align-items: center;
    justify-content: space-between;
    border-bottom: ${theme.colors.black[300]} solid 1px;
    height: 8rem;
    padding: 0 32px;
    position: relative;
`;

const StyledBackButton = styled(IconButton)`
    gap: 10px;

    &:active {
        background-color: ${({ theme }) => theme.colors.black[50]};
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const StyledMaskedIcon = styled(MaskedIcon)`
    color: ${({ theme }) => theme.colors.black[700]};
`;

const BackText = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[700]};
    font-weight: 500;
`;

const StyledFooterButtonsWrapper = styled(HeaderButtonsWrapper)`
    white-space: nowrap;
`;

function getTabIndex(tab: AgentCreateTabType) {
    const index = Object.values(AgentCreateTabType).findIndex(_tab => _tab === tab);
    return index === -1 ? 0 : index;
}

export const Header = observer(() => {
    const agentEditorStore = useAgentEditorStore();
    const setOpenCancelEditModal = useSetRecoilState(isCancelAgentEditModalOpen);

    const navigate = useNavigate();
    const locationState = useLocation() as { state?: LocationState; pathname?: string; initialAvatar?: Presenter };

    const { mutateAsync: revertAgent } = useRevertAgent();
    const { getIsAgentStateChanged } = useCompareEditStates();
    const { clearEditor } = useClearEditor();

    const disabled = useRecoilValue(isImageProcessing);

    const handleOnClick = useCallback(async () => {
        try {
            if (agentEditorStore.isAgentEdit) {
                await revertAgent();
            }
        } finally {
            clearEditor();
            navigate(AppRoutes.Agent);
        }
    }, [clearEditor, agentEditorStore.isAgentEdit]);

    const onTabChange = useCallback(
        (newTab: number) => {
            let newTabType = Object.values(AgentCreateTabType).find((_type, i) => i === newTab) ?? AgentCreateTabType.Appearance;
            agentEditorStore.setCurrentTab(newTabType);
        },
        [agentEditorStore.setCurrentTab]
    );

    const handleBack = () => {
        if (!agentEditorStore.isEditorLoading && getIsAgentStateChanged()) {
            setOpenCancelEditModal(true);
        } else {
            handleOnClick();
        }
    };

    const backText = locationState?.state?.from ? AppRouteNames[locationState.state.from as AppPathname] : '';

    return (
        <HeaderContainer>
            <StyledBackButton onClick={handleBack} disabled={agentEditorStore.isCreating}>
                <StyledMaskedIcon src={ArrowLeft} size="32px" />
                <BackText variant="h4">{backText}</BackText>
            </StyledBackButton>
            <PresentersTabs onChange={onTabChange} tab={getTabIndex(agentEditorStore.currentTab)} disabled={disabled} />
            <StyledFooterButtonsWrapper>
                <HeaderButtons onCancelChanges={() => setOpenCancelEditModal(true)} isEditAgent={agentEditorStore.isAgentEdit} />
            </StyledFooterButtonsWrapper>
        </HeaderContainer>
    );
});
